import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.REACT_APP_BACKEND_URL;

export default function SettingsPage({ onClose, isDarkMode, setIsDarkMode, playOnTV, setPlayOnTV, countryFeatures, section, user, loadCountryData }) {
    const getInitialDarkMode = (timezone) => {
        try {
            const hour = DateTime.now().setZone(timezone).hour;
            return hour < 7 || hour >= 19; // dark from 19:00 to 06:59
        } catch (e) {
            console.error("Invalid timezone, falling back to system time.");
            const localHour = new Date().getHours();
            return localHour < 7 || localHour >= 19;
        }
    };

    const [selectedCountry, setSelectedCountry] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    // countryData state for country-specific settings
    const [countryData, setCountryData] = useState({
        link: '',
        instagram: '',
        youtubeLinks: [''],
        visitStatus: '',
        visitCount: 1
    });
    const [haSettings, setHaSettings] = useState({
        token: '',
        device: '',
        url: ''
    });
    const [visitStatusError, setVisitStatusError] = useState(false);
    const [themeMode, setThemeMode] = useState('auto');
    const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    // Country color states
    const [colorBeen, setColorBeen] = useState('#DBB6A6');
    const [colorWant, setColorWant] = useState('#B6C2DB');
    const [colorLived, setColorLived] = useState('#9FDBB6');

    // Fetch country settings from backend when selectedCountry changes
    useEffect(() => {
        if (!selectedCountry || !user?.sub) return;
        const fetchSettings = async () => {
            try {
                const code = selectedCountry.properties.ISO_A3 || selectedCountry.id;
                // Use new endpoint for single country fetch
                const res = await fetch(`${BACKEND_URL}/countries/one?countryCode=${code}&googleId=${user.sub}`);
                if (res.status === 404) {
                    // No country data exists for this user and country
                    setCountryData({
                        link: '',
                        instagram: '',
                        youtubeLinks: [''],
                        visitStatus: '',
                        visitCount: 1
                    });
                    return;
                }
                if (res.ok) {
                    const data = await res.json();
                    // Assume data is a single country object
                    setCountryData({
                        link: data.link || '',
                        instagram: data.instagram || '',
                        youtubeLinks: Array.isArray(data.youtube) && data.youtube.length > 0 ? data.youtube : [''],
                        visitStatus: data.visit_status || '',
                        visitCount: data.visit_count || 1
                    });
                } else {
                    // No data or error, reset to defaults
                    setCountryData({
                        link: '',
                        instagram: '',
                        youtubeLinks: [''],
                        visitStatus: '',
                        visitCount: 1
                    });
                }
            } catch (e) {
                setCountryData({
                    link: '',
                    instagram: '',
                    youtubeLinks: [''],
                    visitStatus: '',
                    visitCount: 1
                });
            }
        };
        fetchSettings();
    }, [selectedCountry, user]);

    useEffect(() => {
        if (!user?.sub) return;

        const fetchHASettings = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/user-settings?googleId=${user.sub}`);
                if (res.ok) {
                    const data = await res.json();
                    setHaSettings({
                        token: data.ha_token || '',
                        device: data.ha_device || '',
                        url: data.ha_url || ''
                    });
                    setPlayOnTV(data.cast_to_ha || false);
                    setThemeMode(data.theme_mode || 'auto');
                    setTimezone(data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
                    setColorBeen(data.color_been || '#DBB6A6');
                    setColorWant(data.color_want || '#B6C2DB');
                    setColorLived(data.color_lived || '#9FDBB6');
                    if (data.theme_mode === 'dark') setIsDarkMode(true);
                    else if (data.theme_mode === 'light') setIsDarkMode(false);
                    else setIsDarkMode(getInitialDarkMode(data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone));
                }
            } catch (err) {
                console.error('Error loading HA settings:', err);
            }
        };

        fetchHASettings();
    }, [user]);

    // Handler for theme mode change
    const handleThemeChange = async (mode) => {
        setThemeMode(mode);
        if (mode === 'auto') setIsDarkMode(getInitialDarkMode(timezone));
        else setIsDarkMode(mode === 'dark');

        if (user) {
            await fetch(`${BACKEND_URL}/user-settings/theme`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ googleId: user.sub, themeMode: mode })
            });
        }
    };

    // Handler for timezone change
    const handleTimezoneChange = async (newTz) => {
        setTimezone(newTz);
        if (user) {
            await fetch(`${BACKEND_URL}/user-settings/timezone`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ googleId: user.sub, timezone: newTz })
            });
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: isDarkMode ? '#121212' : '#ffffff',
            color: isDarkMode ? '#ffffff' : '#000000',
            zIndex: 4000,
            padding: '20px',
            overflowY: 'auto',
            boxSizing: 'border-box'
        }}>

            {section === 'general' && (
                <div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '20px',
                            cursor: 'pointer'
                        }}
                        onClick={onClose}
                    >
                        <span style={{ fontSize: '20px' }}>←</span>
                        <h2 style={{ margin: 0 }}>General</h2>
                    </div>
                    <h3 style={{ marginBottom: '10px' }}>Theme</h3>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        marginBottom: '20px',
                        maxWidth: '100%',
                        boxSizing: 'border-box'
                    }}>
                      {['auto', 'light', 'dark'].map(mode => (
                        <button
                          key={mode}
                          onClick={() => handleThemeChange(mode)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border:
                              (themeMode === mode)
                                ? '2px solid #007bff'
                                : '1px solid #ccc',
                            backgroundColor: 'transparent',
                            color: isDarkMode ? '#fff' : '#000',
                            cursor: 'pointer'
                          }}
                        >
                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </button>
                      ))}
                    </div>

                    {themeMode === 'auto' && (
                      <>
                        <h3 style={{ marginBottom: '10px' }}>Timezone</h3>
                        <div style={{ marginBottom: '20px' }}>
                          <select
                            value={timezone}
                            onChange={(e) => handleTimezoneChange(e.target.value)}
                            style={{
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid #ccc',
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: isDarkMode ? '#333' : '#fff',
                              color: isDarkMode ? '#fff' : '#000'
                            }}>
                            {Intl.supportedValuesOf('timeZone').map((tz) => (
                              <option key={tz} value={tz}>{tz}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    {/* Country Colors color pickers */}
                    <h3 style={{ marginBottom: '10px' }}>Country Colors</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px' }}>Been color:</label>
                        <input
                          type="color"
                          value={colorBeen}
                          onChange={(e) => setColorBeen(e.target.value)}
                          style={{
                            width: '48px',
                            height: '32px',
                            border: 'none',
                            padding: 0,
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px' }}>Want color:</label>
                        <input
                          type="color"
                          value={colorWant}
                          onChange={(e) => setColorWant(e.target.value)}
                          style={{
                            width: '48px',
                            height: '32px',
                            border: 'none',
                            padding: 0,
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px' }}>Lived color:</label>
                        <input
                          type="color"
                          value={colorLived}
                          onChange={(e) => setColorLived(e.target.value)}
                          style={{
                            width: '48px',
                            height: '32px',
                            border: 'none',
                            padding: 0,
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                      <button
                        style={{
                          marginTop: '8px',
                          padding: '8px 20px',
                          fontSize: '14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: '#007bff',
                          color: '#fff',
                          cursor: 'pointer',
                          alignSelf: 'flex-start'
                        }}
                        onClick={async () => {
                          if (!user) return;
                          try {
                            await fetch(`${BACKEND_URL}/user-settings/colors`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                googleId: user.sub,
                                color_been: colorBeen,
                                color_want: colorWant,
                                color_lived: colorLived
                              })
                            });
                          } catch (error) {
                            console.error('Failed to save color settings:', error);
                          }
                        }}
                      >
                        Save Colors
                      </button>
                    </div>
                </div>
            )}

            {section === 'countries' && (() => {
                if (!selectedCountry) {
                    return (
                        <div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '20px',
                                    cursor: 'pointer'
                                }}
                                onClick={onClose}
                            >
                                <span style={{ fontSize: '20px' }}>←</span>
                                <h2 style={{ margin: 0 }}>Countries</h2>
                            </div>
                            <input
                              type="text"
                              placeholder="Search countries..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid #ccc',
                                marginBottom: '16px',
                                backgroundColor: isDarkMode ? '#333' : '#fff',
                                color: isDarkMode ? '#fff' : '#000',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <div>
                                {countryFeatures
                                  .filter(c => {
                                    const name = c.properties.ADMIN || c.properties.name || c.properties.ISO_A3;
                                    return name.toLowerCase().includes(searchTerm.toLowerCase());
                                  })
                                  .map(c => {
                                    const countryName = c.properties.ADMIN || c.properties.name || c.properties.ISO_A3;
                                    return (
                                        <div
                                            key={c.properties.ISO_A3}
                                            style={{
                                              padding: '12px 0',
                                              borderBottom: '1px solid #ffffff33',
                                              cursor: 'pointer',
                                              fontSize: '16px'
                                            }}
                                            onClick={() => setSelectedCountry(c)}
                                        >
                                            {countryName}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                } else {
                    const countryName = selectedCountry.properties.ADMIN || selectedCountry.properties.name || selectedCountry.properties.ISO_A3;
                    return (
                        <div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '20px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSelectedCountry(null)}
                            >
                                <span style={{ fontSize: '20px' }}>←</span>
                                <h2 style={{ margin: 0 }}>{countryName}</h2>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {/* Visit Status section */}
                                <div>
                                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Visit Status:</label>
                                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    {['want', 'lived', 'been'].map(status => (
                                      <button
                                        key={status}
                                        onClick={() =>
                                            setCountryData({
                                                ...countryData,
                                                visitStatus: countryData.visitStatus === status ? '' : status
                                            })
                                        }
                                        style={{
                                          padding: '8px 16px',
                                          borderRadius: '6px',
                                          border: countryData.visitStatus === status ? '2px solid #007bff' : '1px solid #ccc',
                                          backgroundColor: 'transparent',
                                          color: isDarkMode ? '#fff' : '#000',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                      </button>
                                    ))}
                                  </div>
                                  {visitStatusError && (
                                    <div style={{ color: 'red', marginBottom: '10px' }}>
                                      Please select a visit status before saving.
                                    </div>
                                  )}
                                  {countryData.visitStatus?.toLowerCase() === 'been' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                      <label>Visited count:</label>
                                      <button
                                        onClick={() =>
                                          setCountryData({
                                            ...countryData,
                                            visitCount: Math.max((countryData.visitCount || 1) - 1, 1)
                                          })
                                        }
                                      >−</button>
                                      <span>{countryData.visitCount}</span>
                                      <button
                                        onClick={() =>
                                          setCountryData({
                                            ...countryData,
                                            visitCount: (countryData.visitCount || 1) + 1
                                          })
                                        }
                                      >+</button>
                                    </div>
                                  )}
                                </div>
                                {/* Website link section */}
                                <div>
                                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Website link:</label>
                                  <input
                                      type="text"
                                      placeholder="Website link"
                                      style={{
                                          padding: '8px',
                                          borderRadius: '6px',
                                          border: '1px solid #ccc',
                                          backgroundColor: isDarkMode ? '#333' : '#fff',
                                          color: isDarkMode ? '#fff' : '#000',
                                          fontSize: '14px',
                                          boxSizing: 'border-box'
                                      }}
                                      value={countryData.link}
                                      onChange={e => setCountryData({ ...countryData, link: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Instagram link:</label>
                                  <input
                                      type="text"
                                      placeholder="Instagram link"
                                      style={{
                                          padding: '8px',
                                          borderRadius: '6px',
                                          border: '1px solid #ccc',
                                          backgroundColor: isDarkMode ? '#333' : '#fff',
                                          color: isDarkMode ? '#fff' : '#000',
                                          fontSize: '14px',
                                          boxSizing: 'border-box'
                                      }}
                                      value={countryData.instagram}
                                      onChange={e => setCountryData({ ...countryData, instagram: e.target.value })}
                                  />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>YouTube links:</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      {countryData.youtubeLinks.map((link, index) => (
                                        <div key={`youtube-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
                                          <input
                                            type="text"
                                            placeholder="YouTube link"
                                            style={{
                                              flex: 1,
                                              padding: '8px',
                                              borderRadius: '6px',
                                              border: '1px solid #ccc',
                                              backgroundColor: isDarkMode ? '#333' : '#fff',
                                              color: isDarkMode ? '#fff' : '#000',
                                              fontSize: '14px',
                                              boxSizing: 'border-box',
                                              marginRight: '8px'
                                            }}
                                            value={link}
                                            onChange={e => {
                                              const newLinks = [...countryData.youtubeLinks];
                                              newLinks[index] = e.target.value;
                                              setCountryData({ ...countryData, youtubeLinks: newLinks });
                                            }}
                                          />
                                          <button
                                            onClick={() => {
                                              if (countryData.youtubeLinks.length > 1) {
                                                const newLinks = countryData.youtubeLinks.filter((_, i) => i !== index);
                                                setCountryData({ ...countryData, youtubeLinks: newLinks });
                                              }
                                            }}
                                            style={{ padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}
                                          >−</button>
                                          <button
                                            onClick={() =>
                                              setCountryData({
                                                ...countryData,
                                                youtubeLinks: [...countryData.youtubeLinks, '']
                                              })
                                            }
                                            style={{ padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', marginLeft: '8px' }}
                                          >+</button>
                                        </div>
                                      ))}
                                    </div>
                                </div>
                                <button
                                  style={{
                                    marginTop: '24px',
                                    padding: '10px 24px',
                                    fontSize: '16px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: '#007bff',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    alignSelf: 'flex-start'
                                  }}
                                  onClick={async () => {
                                      if (!user) {
                                          console.error('User is undefined, cannot save settings.');
                                          return;
                                      }

                                      if (!countryData.visitStatus) {
                                          setVisitStatusError(true);
                                          return;
                                      }
                                      setVisitStatusError(false);

                                      console.log('selectedCountry:', selectedCountry);
                                      console.log('user:', user);

                                      const payload = {
                                          ...countryData,
                                          youtube: countryData.youtubeLinks,
                                          countryCode: selectedCountry?.id || '',
                                          googleId: user?.sub || ''
                                      };

                                      console.log("Sending payload:", payload);
                                      await fetch(`${BACKEND_URL}/countries`, {
                                          method: 'POST',
                                          headers: {
                                              'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({
                                              ...countryData,
                                              youtube: countryData.youtubeLinks,
                                              countryCode: selectedCountry?.id || '',
                                              googleId: user?.sub || ''
                                          })
                                      });
                                      if (loadCountryData) loadCountryData();
                                  }}
                                >
                                  Save
                                </button>
                                {countryData.visitStatus && (
                                  <button
                                    style={{
                                      marginTop: '12px',
                                      padding: '8px 20px',
                                      fontSize: '14px',
                                      borderRadius: '6px',
                                      border: '1px solid red',
                                      background: 'transparent',
                                      color: 'red',
                                      cursor: 'pointer',
                                      alignSelf: 'flex-start'
                                    }}
                                    onClick={async () => {
                                      if (!user || !selectedCountry) return;
                                      await fetch(
                                        `${BACKEND_URL}/countries?countryCode=${selectedCountry?.id || ''}&googleId=${user?.sub || ''}`,
                                        {
                                          method: 'DELETE'
                                        }
                                      );
                                      setSelectedCountry(null);
                                    }}
                                  >
                                    Remove Country
                                  </button>
                                )}
                            </div>
                        </div>
                    );
                }
            })()}

            {section === 'ha' && (
                <div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '20px',
                            cursor: 'pointer'
                        }}
                        onClick={onClose}
                    >
                        <span style={{ fontSize: '20px' }}>←</span>
                        <h2 style={{ margin: 0 }}>Home Assistant</h2>
                    </div>
                    <h3>Home Assistant</h3>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input type="checkbox" checked={playOnTV} onChange={(e) => setPlayOnTV(e.target.checked)} />
                        Enable Cast
                    </label>

                    {playOnTV && (
                        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Token:</label>
                                <input
                                    type="text"
                                    placeholder="Enter token"
                                    value={haSettings.token}
                                    onChange={(e) => setHaSettings({ ...haSettings, token: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #ccc',
                                        backgroundColor: isDarkMode ? '#333' : '#fff',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Device name:</label>
                                <input
                                    type="text"
                                    placeholder="Enter device name"
                                    value={haSettings.device}
                                    onChange={(e) => setHaSettings({ ...haSettings, device: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #ccc',
                                        backgroundColor: isDarkMode ? '#333' : '#fff',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>URL:</label>
                                <input
                                    type="text"
                                    placeholder="Enter URL"
                                    value={haSettings.url}
                                    onChange={(e) => setHaSettings({ ...haSettings, url: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #ccc',
                                        backgroundColor: isDarkMode ? '#333' : '#fff',
                                        color: isDarkMode ? '#fff' : '#000',
                                        fontSize: '14px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    {/* Save button for Home Assistant */}
                    <button
                      style={{
                        marginTop: '24px',
                        padding: '10px 24px',
                        fontSize: '16px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#007bff',
                        color: '#fff',
                        cursor: 'pointer',
                        alignSelf: 'flex-start'
                      }}
                      onClick={async () => {
                        if (!user) {
                          console.error('User is undefined, cannot save HA settings.');
                          return;
                        }

                        const payload = {
                          googleId: user.sub,
                          castToHA: playOnTV,
                          token: haSettings.token,
                          device: haSettings.device,
                          url: haSettings.url
                        };

                        try {
                          await fetch(`${BACKEND_URL}/user-settings/ha`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(payload)
                          });
                        } catch (error) {
                          console.error('Failed to save HA settings:', error);
                        }
                      }}
                    >
                      Save
                    </button>
                </div>
            )}
        </div>
    );
}